import React, { useEffect, useState } from "react";
import useSWR, { mutate } from "swr";
import { config } from "~/core";
import Table from "../components/tables/Table";
import InspectionPreAddNoteModal from "../components/Modal/InspectionPreAddNote";
import { apiProvider } from "../api/core/provider";
import EditInspectionPriceModal from "../components/Modal/EditInspectionPrice";
import formatMoney from "../utilities/formatMoney";

export default function InspectorInvoicePrices() {

  const BASE_URL = "/api/" + config.api.version;

  const editPrice = type => {
    setEditingInspectionType(type);
    setEditPriceOpen(true);
  };

  const editCondoPrice = type => {
    setEditingCondoType(type);
    setEditCondoPriceOpen(true);
  };

  const savePriceHandler = (inspectionType, newPrice, updateAll, effectiveDate = null, condo = false) => {
    const payload = {
      type: inspectionType.ID,
      price: Number(newPrice * 100),
      updateAll: updateAll,
      condo: condo
    };

    if (effectiveDate) {
      payload.effectiveDate = effectiveDate;
    }

    apiProvider.post(`accounting/prices/inspector/${selectedInspector}`,
      payload).then((data) => {
      mutate(`${BASE_URL}/accounting/prices/inspector/${selectedInspector}/with-defaults`);
    }
    ).catch((e) => {
      console.log("Error saving price - ", e);
    });
  };

  const [inspectors, setInspectors] = useState(null);
  const [selectedInspector, setSelectedInspector] = useState(null);
  const [editingInspectionType, setEditingInspectionType] = useState(null);
  const [editPriceOpen, setEditPriceOpen] = useState(false);
  const [editingCondoType, setEditingCondoType] = useState(null);
  const [editCondoPriceOpen, setEditCondoPriceOpen] = useState(false);
  const [prices, setPrices] = useState(null);

  // New: For summary tables
  const [defaultSummary, setDefaultSummary] = useState([]);
  const [overridesSummary, setOverridesSummary] = useState([]);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [summaryError, setSummaryError] = useState(null);

  const cols = [
    { Header: "Inspection Type", accessor: "InspectionType" },
    { Header: "Inspector Price", accessor: "FormattedPrice" },
    { Header: "Inspector Condo Price", accessor: "FormattedCondoPriceWithEdit" },
    { Header: "Default Price", accessor: "FormattedDefaultPrice" },
    { Header: "Status", accessor: "priceStatus" },
    { Header: "", accessor: "edit" },
  ];

  // Summary table columns
  const defaultCols = [
    { Header: "Inspection Type", accessor: "InspectionType" },
    { Header: "Default Inspector Price", accessor: "FormattedDefaultInspectorPrice" },
    { Header: "Default Inspector Condo Price", accessor: "FormattedDefaultInspectorCondoPrice" },
  ];
  const overridesCols = [
    { Header: "Inspector", accessor: "InspectorName" },
    { Header: "Inspection Type", accessor: "InspectionType" },
    { Header: "Inspector Price", accessor: "FormattedInspectorPrice" },
    { Header: "Default Price", accessor: "FormattedDefaultPrice" },
    { Header: "Inspector Condo Price", accessor: "FormattedInspectorCondoPrice" },
    { Header: "Default Condo Price", accessor: "FormattedDefaultCondoPrice" },
  ];

  const dataLoaded = (data, key, config) => {
    if (!data || !data.result) {
      return;
    }
    let prices = data.result;
    prices.sort((a, b) => {
      if (a.inspectionType.InspectionType < b.inspectionType.InspectionType) {
        return -1;
      }
      if (a.inspectionType.InspectionType > b.inspectionType.InspectionType) {
        return 1;
      }
      return 0;
    });

    const processedPrices = prices.map(item => {
      // The API returns InspectorPriceWithDefault objects with camelCase fields
      const inspectionType = item.inspectionType;
      const inspectorPrice = item.inspectorPrice;
      const defaultPrice = item.defaultPrice;
      
      return {
        ID: inspectionType.ID,
        InspectionType: inspectionType.InspectionType,
        Price: inspectorPrice,
        CondoPrice: item.inspectorCondoPrice || 0,
        DefaultCondoPrice: item.defaultCondoPrice || 0,
        defaultPrice: defaultPrice,
        FormattedPrice: formatMoney(Number(inspectorPrice) / 100),
        FormattedCondoPrice: item.inspectorCondoPrice || item.defaultCondoPrice ? formatMoney(Number(item.inspectorCondoPrice || item.defaultCondoPrice) / 100) : "",
        FormattedDefaultPrice: formatMoney(Number(defaultPrice) / 100),
        FormattedCondoPriceWithEdit:
          <div className="tw-flex tw-justify-between tw-items-center tw-w-full" style={{ width: 100 }}>
            <span className="tw-block tw-min-w-[32px]">{item.inspectorCondoPrice || item.defaultCondoPrice ? formatMoney(Number(item.inspectorCondoPrice || item.defaultCondoPrice) / 100) : ""}</span>
            <button
              className="btn btn-primary btn-sm"
              onClick={() => editCondoPrice({
                ID: inspectionType.ID,
                InspectionType: inspectionType.InspectionType,
                CondoPrice: (item.inspectorCondoPrice || 0) / 100
              })}
            >
              Edit
            </button>
          </div>,
        differsFromDefault: item.differsFromDefault,
        priceStatus: item.differsFromDefault ? 
          <span className="tw-text-orange-600 tw-font-medium">
            ⚠️ Differs from default
          </span> :
          <span className="tw-text-green-600 tw-font-medium">
            ✓ Default price
          </span>,
        edit: <button
          className="btn btn-primary"
          onClick={() => editPrice({
            ID: inspectionType.ID,
            InspectionType: inspectionType.InspectionType,
            Price: inspectorPrice / 100
          })}
        >
          Edit
        </button>
      };
    });
    
    setPrices(processedPrices);
  };

  const inspectorsLoaded = (data, key, config) => {
    if (data && data.result) {
      setInspectors(data.result);
    }
  };

  // Fetch summary data when no inspector is selected
  useEffect(() => {
    if (selectedInspector && selectedInspector !== "0") {return;}
    setLoadingSummary(true);
    setSummaryError(null);
    // Fetch all inspection types and default inspector prices
    Promise.all([
      apiProvider.get(`accounting/prices`),
      apiProvider.get(`accounting/prices/default-inspector`),
      apiProvider.get(`inspectors`)
    ]).then(async ([typesRes, defaultRes, inspectorsRes]) => {
      const types = typesRes.data.result || [];
      const defaultPrices = defaultRes.data.result || [];
      const inspectors = inspectorsRes.data.result || [];
      // Map of default prices by inspection type ID (regular prices only)
      const defaultPriceMap = {};
      const defaultCondoPriceMap = {};
      defaultPrices.forEach(item => {
        if (!item.Condo) {
          defaultPriceMap[item.InspectionTypeId] = item.Price;
        } else {
          defaultCondoPriceMap[item.InspectionTypeId] = item.Price;
        }
      });
      // Build default summary table
      const defaultSummaryRows = types.map(type => ({
        InspectionType: type.InspectionType,
        FormattedDefaultInspectorPrice: formatMoney((defaultPriceMap[type.ID] || 0) / 100),
        FormattedDefaultInspectorCondoPrice: defaultCondoPriceMap[type.ID] ? formatMoney(defaultCondoPriceMap[type.ID] / 100) : ""
      }));
      setDefaultSummary(defaultSummaryRows);
      // Build overrides summary table
      let overrides = [];
      // For each inspector, fetch their prices with defaults
      try {
        await Promise.all(inspectors.map(async inspector => {
          const res = await apiProvider.get(`accounting/prices/inspector/${inspector.id}/with-defaults`);
          const prices = res.data.result || [];
          prices.forEach(item => {
            const inspectorCondoPrice = item.inspectorCondoPrice || 0;
            const defaultCondoPrice = item.defaultCondoPrice || 0;
            const condoOverride = inspectorCondoPrice > 0 && inspectorCondoPrice !== defaultCondoPrice;
            if (item.differsFromDefault || condoOverride) {
              overrides.push({
                InspectorName: inspector.name,
                InspectionType: item.inspectionType.InspectionType,
                FormattedInspectorPrice: formatMoney(item.inspectorPrice / 100),
                FormattedDefaultPrice: formatMoney(item.defaultPrice / 100),
                FormattedInspectorCondoPrice: inspectorCondoPrice ? formatMoney(inspectorCondoPrice / 100) : "",
                FormattedDefaultCondoPrice: defaultCondoPrice ? formatMoney(defaultCondoPrice / 100) : ""
              });
            }
          });
        }));
        // Sort by inspector name, then inspection type
        overrides.sort((a, b) => {
          if (a.InspectorName < b.InspectorName) {return -1;}
          if (a.InspectorName > b.InspectorName) {return 1;}
          if (a.InspectionType < b.InspectionType) {return -1;}
          if (a.InspectionType > b.InspectionType) {return 1;}
          return 0;
        });
        setOverridesSummary(overrides);
      } catch (error) {
        console.error("Error fetching inspector overrides:", error);
        setSummaryError("Failed to load inspector price overrides. Please try again.");
      }
      setLoadingSummary(false);
    }).catch((error) => {
      console.error("Error fetching summary data:", error);
      setSummaryError("Failed to load summary data. Please try again.");
      setLoadingSummary(false);
    });
  }, [selectedInspector]);

  useSWR(() => selectedInspector && selectedInspector !== "0" ? `${BASE_URL}/accounting/prices/inspector/${selectedInspector}/with-defaults` : null, { onSuccess: dataLoaded });
  useSWR(`${BASE_URL}/inspectors`, { refreshInterval: 0, onSuccess: inspectorsLoaded });

  return (
    <>
      <h1>Inspector Invoice Prices</h1>

      <div className={"tw-mt-10 tw-flex tw-gap-8 tw-text-lg tw-items-baseline"}>
        <p>Inspector:</p>
        {inspectors &&
                <select className="tw-flex-grow tw-rounded-lg"
                  onChange={(e) => setSelectedInspector(e.target.value)}>
                  <option value={0}>Choose inspector..</option>
                  {inspectors && inspectors.map((inspector) =>
                    <option key={inspector.id} value={inspector.id}>{inspector.name}</option>
                  )}
                </select>
        }
      </div>

      {/* Show summary tables if no inspector is selected */}
      {(!selectedInspector || selectedInspector === "0") && 
        <div className="tw-mt-14 tw-mb-10">
          <h2 className="tw-text-xl tw-font-bold tw-mb-4">Default Inspector Prices</h2>
          <Table data={defaultSummary} columns={defaultCols} />
          <h2 className="tw-text-xl tw-font-bold tw-mt-10 tw-mb-4">Inspector Price Overrides</h2>
          {loadingSummary ? <div>Loading...</div> :
            summaryError ? 
              <div className="tw-text-red-600 tw-bg-red-50 tw-p-4 tw-rounded-lg tw-border tw-border-red-200">
                {summaryError}
              </div> :
              <Table data={overridesSummary} columns={overridesCols} />
          }
        </div>
      }

      {/* Show inspector-specific table if inspector is selected */}
      {selectedInspector && selectedInspector !== "0" && prices &&
            <div className={"tw-mt-14 tw-mb-10"}>
              <Table data={prices} columns={cols}/>
            </div>
      }

      <EditInspectionPriceModal inspectionType={editingInspectionType} open={editPriceOpen}
        closeHandler={() => setEditPriceOpen(false)}
        saveHandler={savePriceHandler}/>

      <EditInspectionPriceModal inspectionType={editingCondoType} open={editCondoPriceOpen}
        closeHandler={() => setEditCondoPriceOpen(false)}
        saveHandler={(type, price, updateAll, date) => savePriceHandler(type, price, updateAll, date, true)}
        priceField="CondoPrice"
        title="Edit Condo Price for"
        initialPrice={editingCondoType?.CondoPrice || 0}/>
    </>
  );
}
